import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Tooltip } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { surveyExport, surveysDeleteByIds } from 'api/endpoints/survey';
import Button from 'components/Button';
import Table, { IndeterminateCheckbox } from 'components/Table';
import { ENUM_SURVEY_STATUS, ENUM_TYPE_DOWNLOAD } from 'constants/enum';
import { FORMAT_TIME } from 'constants/index';
import { TEXT_COMMON, TEXT_CREATE_SURVEY, TEXT_DOWNLOAD, TEXT_SURVEY_LIST } from 'constants/string/texts';
import { useClickOutside } from 'hooks/useClickOutside';
import useSurvey from 'hooks/useSurveys';
import { toastError, toastSuccess } from 'lib/toast';
import Duplicate from 'pages/Survey/_components/Duplicate';
import AppRoutes from 'routes/routes';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { setOpenCancelSchedule, setOpenSchedule } from 'store/actions/survey';
import { checkIsShowScheduleText, convertStatusSurvey, convertTimePublic, getSurveyKindLabel } from 'utils/helper';

const ControlSurveyItem = ({ item, reloadData, deleteSurvey, onPageChange }) => {
  const dispatch = useDispatch();
  const [openControlSurveyItem, setIsOpenControlSurveyItem] = useState(false);
  const controlSurveyItemRef = useRef(null);
  const contentControlSurveyItemRef = useRef(null);
  const { makePrivate, revertPublic } = useSurvey();

  useClickOutside(controlSurveyItemRef, () => setIsOpenControlSurveyItem(false));

  const privateSurvey = async () => {
    makePrivate(item.id)
      .then(async () => {
        if (onPageChange) {
          const result = await onPageChange(1);
          if (!result) {
            if (reloadData) {
              await reloadData();
            }
          }
        } else if (reloadData) {
          await reloadData();
        }
        window.scrollTo(0, 0);
      })
      .catch(() => {
        return false;
      });
  };

  const revertPublicSurvey = async () => {
    revertPublic(item.id, item.title)
      .then(async () => {
        if (onPageChange) {
          const result = await onPageChange(1);
          if (!result) {
            if (reloadData) {
              await reloadData();
            }
          }
        } else if (reloadData) {
          await reloadData();
        }
        window.scrollTo(0, 0);
      })
      .catch(() => {
        return false;
      });
  };

  const publicSurvey = async () => {
    const callback = async () => {
      if (onPageChange) {
        const result = await onPageChange(1);
        if (!result) {
          if (reloadData) {
            await reloadData();
          }
        }
      } else if (reloadData) {
        await reloadData();
      }
    };

    dispatch(
      setOpenSchedule({
        isOpen: true,
        data: item,
        callBack: callback,
      })
    );
  };

  const handleDownload = async (export_type) => {
    dispatch(setLoading({ isOpen: true }));
    await surveyExport({ survey_ids: [item.id] }, export_type)
      .then((res) => {
        dispatch(setLoading({ isOpen: false }));
      })
      .catch((error) => {
        toastError(error.message);
        dispatch(setLoading({ isOpen: false }));
      });
  };

  return (
    <div className="flex-between-center text-sm">
      <div className="relative flex items-center">
        <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlSurveyItem((prev) => !prev)} ref={controlSurveyItemRef}>
          <img src={require('assets/resources/MoreVert.svg').default} alt="more vert icon" width={24} height={24} />
        </button>
        <div
          ref={contentControlSurveyItemRef}
          className={`absolute z-2 right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlSurveyItem ? 'show' : ''}`}
        >
          <ul>
            {item.status !== ENUM_SURVEY_STATUS.DRAFT && (
              <>
                <li
                  onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.EXCEL)}
                  className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                >
                  {TEXT_DOWNLOAD.EXCEL_DOWNLOAD}
                </li>
                <li
                  onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.CSV)}
                  className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                >
                  {TEXT_DOWNLOAD.CSV_DOWNLOAD}
                </li>
              </>
            )}
            {item.status === ENUM_SURVEY_STATUS.PUBLIC && (
              <li onClick={privateSurvey} className="px-3 w-48 py-1.25 text-danger-245 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer">
                {TEXT_COMMON.UNPUBLIC_TEXT}
              </li>
            )}
            {item.status === ENUM_SURVEY_STATUS.DRAFT && (
              <li onClick={publicSurvey} className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer">
                {TEXT_CREATE_SURVEY.SAVE_PUBLIC}
              </li>
            )}
            {item.status === ENUM_SURVEY_STATUS.PRIVATE && (
              <li
                onClick={revertPublicSurvey}
                className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
              >
                {TEXT_SURVEY_LIST.REVERT_PUBLIC}
              </li>
            )}
            {item.status !== ENUM_SURVEY_STATUS.PUBLIC && (
              <li
                onClick={() => {
                  deleteSurvey(item.id);
                }}
                className="px-3 w-48 py-1.75 text-danger-229 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
              >
                {TEXT_COMMON.DELETE}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

const SurveyListTable = ({ data, setSort, reloadData, sort, page, keyword, isFilter, onPageChange }) => {
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([{ page: page, selected: [] }]);
  const [openModalDuplicate, setOpenModalDuplicate] = useState(false);
  const [dataClone, setDataClone] = useState({});
  const { deleteSurvey } = useSurvey();

  const getDataNoPublic = (table) => {
    const rows = table.getRowModel().rows;
    const result = _.filter(rows, (item) => item.original.status !== ENUM_SURVEY_STATUS.PUBLIC);
    return result;
  };

  const handleRowSelect = (rowSelect) => {
    try {
      setSelectedRows((prevSelected) => {
        // Get item index with page number
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        // Create new object and push to array
        if (pageSelectedExists === -1) {
          return [
            ...prevSelected,
            {
              page: page,
              selected: [rowSelect?.original?.id],
            },
          ];
        }

        const rowIsSelected = selectedRowWithPage[pageSelectedExists]?.selected?.find((row) => row === rowSelect?.original?.id);

        /**
         * If you are select
         * - Delete the selected item from the array
         * - Delete all Object contains lines
         * - Add new object
         */
        if (rowIsSelected) {
          const selectedRemovedRow = selectedRowWithPage[pageSelectedExists].selected.filter((row) => row !== rowSelect.original.id);
          selectedRowWithPage.splice(pageSelectedExists, 1);
          selectedRowWithPage.push({
            page: page,
            selected: selectedRemovedRow,
          });
          return selectedRowWithPage;
        }

        const rowSelected = selectedRowWithPage[pageSelectedExists].selected;
        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: [...rowSelected, rowSelect.original.id],
        });
        return selectedRowWithPage;
      });
      rowSelect.getToggleSelectedHandler();
    } catch {
      // Handle error select
    }
  };

  const handleSelectAll = (table) => {
    try {
      setSelectedRows((prevState) => {
        const dataNoPublic = getDataNoPublic(table);
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        if (pageSelectedExists === -1) {
          return [
            ...prevState,
            {
              page: page,
              selected: dataNoPublic?.map((row) => row?.original?.id),
            },
          ];
        }

        /**
         * If the Select of the page is currently in the Testhall (Condition) state
         * (Condition): Select length is equal to the number of non -public status items on the current page
         * then remove the whole object of that page to become Uncheck.
         */
        if (selectedRowWithPage[pageSelectedExists]?.selected?.length === dataNoPublic?.length) {
          selectedRowWithPage.splice(pageSelectedExists, 1);
          return selectedRowWithPage;
        }

        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: dataNoPublic.map((row) => row?.original?.id),
        });

        return selectedRowWithPage;
      });
    } catch {
      // Handle error select all
    }
  };

  /**
   * Delete a survey
   * @param {*} idSurvey
   */
  const onClickDeleteSurvey = (idSurvey) => {
    const confirmDeleteSurvey = async () => {
      try {
        deleteSurvey(idSurvey)
          .then(async () => {
            await reloadData();
            setSelectedRows(() => {
              const selectedRowWithPage = selectedRows.concat();

              const pageSelectedExists = _.findIndex(selectedRowWithPage, { page: page });

              if (pageSelectedExists !== -1) {
                const selectedRemovedRow = selectedRowWithPage[pageSelectedExists]?.selected?.filter((row) => row !== idSurvey);
                selectedRowWithPage?.splice(pageSelectedExists, 1);

                selectedRowWithPage?.push({
                  page: page,
                  selected: selectedRemovedRow,
                });
                return selectedRowWithPage;
              }
              return [];
            });
            dispatch(clearModal());
          })
          .catch((errors) => {
            dispatch(clearModal());
          });
      } catch {
        // Handle Error
        dispatch(setLoading({ isOpen: false }));
      }
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: TEXT_SURVEY_LIST.CONFIRM_DELETE_SURVEY,
        confirmText: TEXT_COMMON.DELETE,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        onConfirm: confirmDeleteSurvey,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  /**
   * Delete multiple survey with selected
   */
  const onClickDeleteSurveys = () => {
    const confirmDeleteSurveys = async () => {
      try {
        dispatch(setLoading({ isOpen: true }));
        await surveysDeleteByIds({ ids: listSurveysDelete })
          .then(() => {
            reloadData();
            toastSuccess(`${listSurveysDelete.length}${TEXT_SURVEY_LIST.SELECTED_DELETED}`);
            dispatch(clearModal());
            setSelectedRows([]);
          })
          .catch((err) => {
            toastError(err?.message);
            dispatch(clearModal());
          });
        dispatch(setLoading({ isOpen: false }));
      } catch {
        // Handle error
        dispatch(setLoading({ isOpen: false }));
      }
    };

    const listSurveysDelete = _.flatMap(selectedRows, 'selected');
    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: TEXT_SURVEY_LIST.CONFIRM_DELETE_SURVEY,
        confirmText: TEXT_COMMON.DELETE,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        onConfirm: confirmDeleteSurveys,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  const onClickCancelSchedule = (id) => {
    dispatch(setOpenCancelSchedule({ isOpen: true, id: id, callBack: reloadData }));
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: '-',
        header: ({ table }) => (
          <div className={`${getDataNoPublic(table)?.length <= 0 && '!cursor-not-allowed'} flex justify-center items-center w-4`}>
            <IndeterminateCheckbox
              className={`m-auto  ${getDataNoPublic(table)?.length <= 0 && '!pointer-events-none'}`}
              {...{
                checked: _.find(selectedRows, { page: page })?.selected?.length === getDataNoPublic(table)?.length && data?.length > 0,
                indeterminate: false,
                onChange: () => handleSelectAll(table),
              }}
            />
          </div>
        ),
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="flex justify-center">
              <IndeterminateCheckbox
                className={`m-auto ${row.original.status !== ENUM_SURVEY_STATUS.PUBLIC ? '' : 'opacity-50 !cursor-not-allowed'}`}
                {...{
                  checked:
                    row?.original?.status !== ENUM_SURVEY_STATUS.PUBLIC
                      ? _.find(selectedRows, { page: page })?.selected?.some((item) => item === row?.original?.id)
                      : false,
                  indeterminate: false,
                  onChange: () => row?.original?.status !== ENUM_SURVEY_STATUS.PUBLIC && handleRowSelect(row),
                }}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'title',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{TEXT_SURVEY_LIST.CONSULTATION_TICKET_NAME}</span>;
        },
        cell: ({ row, getValue }) => {
          const type_survey_name = getSurveyKindLabel(row.original);
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className="break-all min-w-32 w-3min-w-32">
                {row?.original?.is_template || (row?.original?.meta_data?.from_template && TEXT_COMMON.FV_TEMPLATE)}
                {getValue()} {type_survey_name && `(${type_survey_name})`}
              </span>
              {checkIsShowScheduleText(row.original?.status, row.original?.public_start_time, row.original?.public_end_time) && (
                <>
                  <Tooltip
                    placement="bottom"
                    overlayStyle={{ maxWidth: '300px' }}
                    overlayInnerStyle={{ fontSize: '14px', background: 'rgba(0, 0, 0, 0.75)' }}
                    showArrow={false}
                    title={convertTimePublic(row.original?.status, row.original?.public_start_time, row.original?.public_end_time)}
                  >
                    <span className="text-sm text-black/85 break-all line-clamp-1">
                      {convertTimePublic(row.original?.status, row.original?.public_start_time, row.original?.public_end_time)}
                    </span>
                  </Tooltip>
                  <button
                    type="button"
                    className="text-sm text-primary-346 text-nowrap whitespace-nowrap"
                    onClick={() => onClickCancelSchedule(row.original?.id)}
                  >
                    {TEXT_CREATE_SURVEY.CANCEL_PUBLIC_SETTING}
                  </button>
                </>
              )}
              <span
                className={`text-xs rounded-sm flex-middle-center border border-solid min-w-13 min-h-5.75 ${convertStatusSurvey(row?.original?.status)?.class}`}
              >
                {convertStatusSurvey(row.original.status).name}
              </span>
            </div>
          );
        },
        size: 544,
        sortingFn: () => {},
      },
      {
        accessorKey: 'updated_at',
        size: 144,
        header: () => <span>{TEXT_SURVEY_LIST.EDITING_DATE}</span>,
        cell: ({ row }) => <span>{moment(row.original.updated_at).format(FORMAT_TIME.DATE)}</span>,
        sortingFn: () => {},
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 80,
        cell: ({ row }) => (
          <Link to={`${AppRoutes.surveyViewFn(row?.original?.id)}`} className="text-center rounded-sm py-0.5 px-2 cursor-pointer text-primary-346">
            {TEXT_COMMON.VIEW}
          </Link>
        ),
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 80,
        cell: ({ row }) => (
          <div className="flex-between-center text-sm">
            {row?.original?.status === ENUM_SURVEY_STATUS.PUBLIC ? (
              <Tooltip
                placement="top"
                overlayStyle={{ maxWidth: 'unset' }}
                overlayInnerStyle={{ whiteSpace: 'nowrap', fontSize: '14px', background: 'rgba(0, 0, 0, 0.75)' }}
                title={TEXT_SURVEY_LIST.TOOLTIP_CAN_BE_EDIT}
              >
                <span className="text-center rounded-sm py-0.5 px-2 cursor-not-allowed text-gray-651">{TEXT_COMMON.EDIT}</span>
              </Tooltip>
            ) : (
              <Link
                to={`${AppRoutes.surveyEditFn(row?.original?.id)}`}
                className="text-center rounded-sm py-0.5 px-2 cursor-pointer text-primary-346"
              >
                {TEXT_COMMON.EDIT}
              </Link>
            )}
          </div>
        ),
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 80,
        cell: ({ row }) => (
          <div className="flex-between-center text-sm">
            <span
              className="text-center rounded-sm py-0.5 px-2 cursor-pointer text-primary-346"
              onClick={() => {
                setOpenModalDuplicate(true);
                setDataClone(row.original);
              }}
            >
              {TEXT_COMMON.DUPLICATE}
            </span>
          </div>
        ),
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 80,
        cell: ({ row }) => (
          <ControlSurveyItem onPageChange={onPageChange} deleteSurvey={onClickDeleteSurvey} reloadData={reloadData} item={row?.original || {}} />
        ),
      },
    ],
    [selectedRows, page]
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [sort, keyword]);

  return (
    <>
      {_.flatMap(selectedRows, 'selected').length > 0 && (
        <>
          <div className="p-4 w-full border-2 border-solid border-primary-423 rounded-md mb-2 flex justify-between items-center h-16">
            <p className="text-secondary-354 text-sm">
              {_.flatMap(selectedRows, 'selected').length} {TEXT_SURVEY_LIST.SELECTING}
            </p>
            <div className="flex">
              <div className="flex gap-4">
                <Button
                  className="text-sm w-21 h-8"
                  color="danger-outline"
                  text={TEXT_COMMON.DELETE}
                  onClick={onClickDeleteSurveys}
                  icon={<img className="mr-2.5" src={require('assets/resources/Trash.svg').default} alt="trash icon" width={14} height={14} />}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <Table columns={columns} data={data} isFilter={isFilter} />
      {openModalDuplicate && (
        <Duplicate
          onPageChange={onPageChange}
          reloadData={reloadData}
          form={dataClone}
          close={() => {
            setOpenModalDuplicate(false);
          }}
        />
      )}
    </>
  );
};

export default React.memo(SurveyListTable);
